// Shared bounded-wait helper for the weak-network LM graph prep (TT-1283). Used by both
// storyLmGraphDownloader.ensurePhrase (bounding the single-phrase re-download) and
// ReaderManager's end-of-phrase / launch prep (bounding the readiness warm), so the two
// paths cannot drift into subtly different race semantics.

/**
 * Resolves `promise`, or `fallback` if `promise` has not settled within `budgetMs`. The
 * underlying promise keeps running — its late result is simply ignored once the budget
 * wins — so a backgrounded download/warm can still land for a later read. A rejection
 * propagates to the caller.
 *
 * `budgetMs` null/undefined awaits the promise fully (no bound); `budgetMs <= 0` means the
 * deadline is already spent and resolves `fallback` immediately (it does NOT fall through
 * to an unbounded await).
 *
 * @template T
 * @param {Promise<T>|T} promise
 * @param {number} [budgetMs]
 * @param {T} fallback Value resolved when the budget expires first / is already spent.
 * @returns {Promise<T>}
 */
export function withBudget(promise, budgetMs, fallback) {
  // Distinguish "no budget given" from "budget already spent": null/undefined means await
  // the promise fully (an unbounded caller), while a non-positive budget means the deadline
  // is exhausted so resolve the fallback immediately — never fall through to an unbounded
  // await for a 0ms budget (that would reintroduce the exact stall this bounds).
  if (budgetMs == null) {
    return Promise.resolve(promise);
  }
  if (budgetMs <= 0) {
    // The result is intentionally ignored; swallow a late rejection so it is not unhandled.
    Promise.resolve(promise).catch(() => {});
    return Promise.resolve(fallback);
  }
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => resolve(fallback), budgetMs);
    Promise.resolve(promise).then(
      (value) => { clearTimeout(timer); resolve(value); },
      (err) => { clearTimeout(timer); reject(err); },
    );
  });
}
